// Heygents agent-activity motif primitives (Brand System v0.1 "Graphic motif").
//
// The recurring brand graphic language is work moving through the system:
//   Claude ● running / Codex ✓ completed / Agent 03 -> waiting
//   TASK -> AGENT -> CODE -> TEST -> DONE
// These are the shared, reusable building blocks. Technical labels (agent,
// repo, branch, status, command) render in Geist Mono; state colour comes from
// the semantic tokens in tokens.css. No robots/brains/sparkles.

// Semantic states -> token-backed CSS classes. Brand mapping:
//   success/done = green, running = blue/cyan, waiting/attention = amber,
//   failure/error = red, idle = neutral.
const STATE_ALIAS = {
  running: "running", run: "running", active: "running",
  done: "done", success: "done", completed: "done", ok: "done", passed: "done",
  waiting: "waiting", attention: "waiting", pending: "waiting", approval: "waiting",
  error: "error", failure: "error", failed: "error",
  idle: "idle", queued: "idle",
};
function stateClass(state){ return STATE_ALIAS[String(state || "idle").toLowerCase()] || "idle"; }

// A single status dot + optional label, e.g. <StatusDot state="running" label="Claude" />
// A "running" dot pulses (activity), everything else is steady.
function StatusDot({ state = "idle", label, glyph, mono = true, className = "" }){
  const sc = stateClass(state);
  return (
    <span className={"sdot sdot--" + sc + (mono ? " mono" : "") + (className ? " " + className : "")}>
      <i className="sdot-i" aria-hidden="true"></i>
      {label != null && <span className="sdot-l">{label}</span>}
      {glyph && <span className="sdot-g" aria-hidden="true">{glyph}</span>}
    </span>
  );
}

// A horizontal execution flow: TASK -> AGENT -> CODE -> TEST -> DONE.
// steps: array of { label, state } (state optional). Steps at/under the active
// index read as done; the active one animates; later ones are idle.
function ExecutionLine({ steps = [], active = -1, vertical = false, className = "" }){
  const items = steps.map((s) => (typeof s === "string" ? { label: s } : s));
  return (
    <div className={"execline" + (vertical ? " execline--v" : "") + (className ? " " + className : "")} role="list">
      {items.map((s, i) => {
        const st = s.state != null ? stateClass(s.state)
          : (active < 0 ? "idle" : i < active ? "done" : i === active ? "running" : "idle");
        return (
          <span className="execstep" key={i} role="listitem">
            {i > 0 && <span className="execarrow" aria-hidden="true">{vertical ? "↓" : "→"}</span>}
            <span className={"execnode execnode--" + st + " mono"}>
              <i className="execnode-i" aria-hidden="true"></i>
              <span className="execnode-l">{s.label}</span>
            </span>
          </span>
        );
      })}
    </div>
  );
}

// The Heygents brand mark: the single brand logo, served as /favicon.svg (Ink
// tile, mint H, cursor caret). Every brand position in the app renders this
// one file so the favicon, PWA icon and in-app logo can never drift apart.
function BrandMark({ size = 22, className = "", alt = "Heygents" }){
  return (
    <img className={"brandmark" + (className ? " " + className : "")} src="/favicon.svg"
         width={size} height={size} alt={alt} draggable="false"/>
  );
}

export { StatusDot, ExecutionLine, BrandMark, stateClass };
